import {
  getInspectionsWithSettings,
  getInspection,
  saveInspection,
  uploadAttachments,
} from 'portal-modules/inspections/api/api'
import { takeLatest, call, put, all } from 'redux-saga/effects'
import { delay } from 'redux-saga'
import { LOGOUT } from 'portal-modules/auth/redux/modules/user'
import createRequestSaga from 'portal-modules/common/redux/create-request-saga'
import createFormAction from 'portal-modules/common/redux/create-form-action'
import { ALERT_TYPES } from 'portal-modules/alerts-panel/constants/types'
import { dismissAlertsByTypeByRef } from 'portal-modules/alerts-panel/redux/modules/alerts'
import { addError } from 'portal-modules/common/redux/modules/errors'
import { addSettings } from 'portal-modules/common/redux/modules/settings'
// action types
export const REQUEST_INSPECTIONS_WITH_SETTINGS =
  'rp/inspections/REQUEST_INSPECTIONS_WITH_SETTINGS'
export const RECEIVE_INSPECTIONS_WITH_SETTINGS =
  'rp/inspections/RECEIVE_INSPECTIONS_WITH_SETTINGS'
export const REQUEST_INSPECTION = 'rp/inspections/REQUEST_INSPECTION'
export const RECEIVE_INSPECTION = 'rp/inspections/RECEIVE_INSPECTION'
export const SAVE_INSPECTION = 'rp/inspections/SAVE_INSPECTION'
export const INSPECTION_SAVED = 'rp/inspections/INSPECTION_SAVED'
export const SAVE_INSPECTION_IMAGE = 'rp/inspections/SAVE_INSPECTION_IMAGE'

// action creators
export const requestInspectionsWithSettings = () => ({
  type: REQUEST_INSPECTIONS_WITH_SETTINGS,
})

export const requestInspection = payload => ({
  type: REQUEST_INSPECTION,
  payload,
})

export const receiveInspectionsWihSettings = payload => ({
  type: RECEIVE_INSPECTIONS_WITH_SETTINGS,
  payload,
})

export const requestSaveInspection = createFormAction(SAVE_INSPECTION)

export const requestSaveInspectionImage = payload => ({
  type: SAVE_INSPECTION_IMAGE,
  payload,
})

// reducer
export default (state = {}, { payload, type }) => {
  switch (type) {
    case RECEIVE_INSPECTIONS_WITH_SETTINGS:
      return {
        ...state,
        ...{
          inspectionPlans: Object.values(payload).reduce(
            (obj, { id, ...rest }) => {
              return {
                ...obj,
                [id]: {
                  ...rest,
                  id,
                },
              }
            },
            {},
          ),
        },
      }
    case RECEIVE_INSPECTION:
      return {
        ...state,
        inspection: {
          ...state['inspection'],
          ...{ [payload.id]: { ...payload } },
        },
      }
    case INSPECTION_SAVED:
      return {
        ...state,
        inspectionPlans: {
          ...state.inspectionPlans,
          [payload.id]: {
            ...state.inspectionPlans[payload.id],
            inspected_on: payload.inspected_on,
            status: 'Review',
          },
        },
        inspection: Object.values(state.inspection).reduce(
          (obj, { id, ...rest }) =>
            id !== payload.id ? { ...obj, [id]: { id, ...rest } } : obj,
          {},
        ),
      }
    case LOGOUT:
      return {}
    default:
      return state
  }
}

// sagas
function* fetchInspectionsWithSettings() {
  const { inspections, settings } = yield call(getInspectionsWithSettings)

  if (inspections.length > 0) {
    yield put(receiveInspectionsWihSettings(inspections))
  }

  if (Object.keys(settings).length > 0) {
    yield put(addSettings({ inspection: settings }))
  }
}

function* fetchInspection({ payload }) {
  const { inspection = {}, message = '', code = 0 } = yield call(getInspection, {
    inspection_id: payload.id,
  })

  if (Object.keys(inspection)?.length) {
    if (inspection?.is_unit_type_setup) {
      inspection?.inspection_locations?.forEach(location => {
        const locationId = location?.property_unit_maintenance_location_id || 0
        location.problems.forEach(problem => {
          problem.id = `${problem.id}-${locationId}`
          problem.property_unit_maintenance_location_id = locationId
        })
      })
    }
    yield put({
      type: RECEIVE_INSPECTION,
      payload: inspection,
    })
    yield put(
      dismissAlertsByTypeByRef({
        refId: inspection.id,
        type: ALERT_TYPES.INSPECTION,
      }),
    ) 
  } else {
    yield code === 6000 && put(addError({ key: 'inspection', message }))
  }
}

function* postSaveInspection({ payload }) {
  const { inspection } = yield call(saveInspection, {
    ...payload.saveInspectionData,
  })

  // We are getting the response_id for the respective inspection_problems from the saveInspection response.
  // Hence here we manipulating the response and setting attachments according to inspection_problems.

  if (payload.inspectionsAttachments.length) {
    const { inspectionsAttachments } = payload
    yield put(
      requestSaveInspectionImage({ inspectionsAttachments, inspection }),
    )
    yield delay(5000)
  }

  if (Object.keys(inspection).length) {
    yield put({
      type: INSPECTION_SAVED,
      payload: inspection,
    })
  }
}

function* postSaveInspectionImage({ payload }) {
  const { inspectionsAttachments, inspection } = payload

  const attachmentDetails = inspectionsAttachments?.map(
    ({
      problem_id,
      attachments,
      property_unit_maintenance_location_id = 0,
    }) => {
      const responseData = inspection?.inspection_responses?.find(
        inspection_response => +inspection_response.problem_id === +problem_id,
      )
      return {
        problem_id: problem_id,
        response_id: responseData?.id || null,
        attachment_data: attachments,
        property_unit_maintenance_location_id,
      }
    },
  )

  //Do API request for uploading attachments.

  let actions = []
  attachmentDetails?.forEach(
    ({
      problem_id,
      response_id,
      attachment_data,
      property_unit_maintenance_location_id,
    }) => {
      const formData = new FormData()
      if (property_unit_maintenance_location_id) {
        formData.append(
          'property_unit_maintenance_location_id',
          property_unit_maintenance_location_id,
        )
      }
      formData.append('inspection_id', inspection.id)
      attachment_data.forEach(image => {
        formData.append(`attachment[${problem_id}][${response_id}][]`, image)
      })
      actions.push(formData)
    },
  )

  yield all(actions.map(requestData => call(uploadAttachments, requestData)))
}

function* watchRequestInspectionsWithSettings() {
  yield takeLatest(
    REQUEST_INSPECTIONS_WITH_SETTINGS,
    createRequestSaga(fetchInspectionsWithSettings, {
      key: 'inspection',
      message: __('Looking up inspections'),
    }),
  )
}

function* watchRequestInspection() {
  yield takeLatest(
    REQUEST_INSPECTION,
    createRequestSaga(fetchInspection, {
      key: 'inspection',
      message: __('Looking up inspection'),
    }),
  )
}

function* watchRequestSaveInspection() {
  yield takeLatest(
    SAVE_INSPECTION,
    createRequestSaga(postSaveInspection, {
      key: 'inspection',
      message: __('Saving inspection'),
      successMessage: __('Inspection successfully saved'),
      write: true,
    }),
  )
}

function* watchRequestSaveInspectionImages() {
  yield takeLatest(
    SAVE_INSPECTION_IMAGE,
    createRequestSaga(postSaveInspectionImage, {
      key: 'inspection_image',
      message: __('Saving inspection Image'),
      successMessage: __('Inspection image uploaded successfully.'),
      write: true,
    }),
  )
}

export const sagas = [
  watchRequestInspectionsWithSettings,
  watchRequestInspection,
  watchRequestSaveInspection,
  watchRequestSaveInspectionImages,
]
